import {
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { reportPreferenceIssues } from '../contract/report';
import { resolvePreferences } from '../contract/resolve';
import type { ResolvedPreferences } from '../contract/types';
import type { PreferenceInput } from '../contract/wire';
import { buildFormatBundle } from '../format/bundle';
import { buildCalendarBundle } from '../format/calendar';
import {
  buildPassThroughBundle,
  buildPassThroughCalendar,
  buildPassThroughPhone,
} from '../format/passthrough';
import { buildPhoneBundle } from '../format/phone';
import {
  LocalizationContext,
  type LocalizationContextValue,
} from './LocalizationContext';
import { clearFormatSnapshot, publishFormatSnapshot } from './snapshot';

export type LocalizationProviderProps = {
  /**
   * The profile payload, as the API returns it.
   *
   * The raw block rather than an already-resolved object, so resolution and its reporting happen in
   * one place however many apps mount this. The shell hands over what it fetched; deciding what the
   * fields mean is the core's job.
   *
   * Read rather than selected from a store because `react-redux` is not federation-shared and each
   * app owns its own store.
   */
  preferences: PreferenceInput;
  /**
   * Whether the rollout flag is on. The flag is named in the package README.
   *
   * Injected rather than read, for the reason `preferences` is: R1 makes the core zero-dependency,
   * so it cannot import `@brightdrop/feature-flags-client` any more than it can import i18next or
   * Redux. The app evaluates the flag and hands over the answer.
   *
   * Off does not unmount anything or stop anything answering. It swaps the format bundle for one
   * that renders as an en-US reader in this reader's own zone, so every call site keeps working
   * and every value is presented the way it was before any of this existed. An en-US reader sees
   * the same screen either way. See {@link buildPassThroughBundle}.
   *
   * Required at the root, so that no app can mount the core and lose the kill switch by omission.
   *
   * A nested provider may pass `undefined` to inherit the enclosing answer, which is the only
   * reason the type still admits it. Passing `enabled={true}` in a subtree opts that subtree back
   * in — inheritance is a default, not a lock — but the root snapshot does not follow:
   * `getFormatSnapshot()` keeps the pass-through bundle, so React and the imperative path would
   * disagree. A nested provider is for scoping preferences; the flag is one answer for the whole
   * application.
   */
  enabled: boolean | undefined;
};

/**
 * Resolves preferences and publishes them, with their formatters, to the tree. Mounted once per
 * app, near the root.
 *
 * Which formatters those are depends on `enabled`. Both candidates reach the context and the
 * imperative snapshot by the same path, so React and non-React callers cannot end up on opposite
 * sides of the flag.
 *
 * Nesting a second provider is allowed and scopes different preferences to a subtree — a preview of
 * another user's units, say. Only the outermost one publishes the imperative snapshot, so
 * `getFormatSnapshot()` always answers for the application rather than for whichever subtree
 * rendered last.
 */
const LocalizationProvider = ({
  preferences,
  enabled: requested,
  children,
}: PropsWithChildren<LocalizationProviderProps>): JSX.Element => {
  const outer = useContext(LocalizationContext);

  // An outer provider means this one scopes a subtree, and the snapshot belongs to the application.
  const isRoot = outer === undefined;

  // Inherited rather than defaulted, so a nested provider cannot hold the subtree on the other side
  // of the flag from the snapshot the root published.
  const enabled = requested ?? outer?.enabled ?? true;

  // Keyed on the prop, so a shell that memoizes resolves once. One that does not pays for a
  // resolution per render of its root — a handful of `Intl` constructions — but still gets a stable
  // bundle, because identity is settled below on the result rather than on the input.
  const { preferences: resolved, issues } = useMemo(
    () => resolvePreferences(preferences),
    [preferences]
  );

  /**
   * Identity keys on the signature, not on the payload.
   *
   * A shell that builds its prop inline — `preferences={{ userPreferences }}` — hands over a new
   * object every render, and memoizing on that would rebuild the bundle every render and break
   * every memo boundary downstream. Holding the first resolution seen for a signature means the
   * guarantee survives however the caller writes the call site, which matters because the caller
   * cannot tell from the outside that it is load-bearing.
   */
  const settled = useRef<ResolvedPreferences>(resolved);
  if (settled.current.signature !== resolved.signature) {
    settled.current = resolved;
  }
  const stable = settled.current;

  /**
   * `enabled` earns its place in the dependency list.
   *
   * The flag is a prop rather than a resolved preference, so `stable.signature` does not move when
   * it flips. Keying identity on the signature alone would hand every memo boundary downstream the
   * bundle from the other side of the flag and keep it there.
   */
  const value = useMemo<LocalizationContextValue>(
    () => ({
      preferences: stable,
      enabled,
      format: enabled
        ? buildFormatBundle(stable)
        : buildPassThroughBundle(stable),
      calendar: enabled
        ? buildCalendarBundle(stable)
        : buildPassThroughCalendar(stable),
      phone: enabled ? buildPhoneBundle(stable) : buildPassThroughPhone(stable),
    }),
    [stable, enabled]
  );

  if (isRoot) {
    publishFormatSnapshot(value.format, enabled);
  }

  // Publishing again here keeps setup and cleanup symmetric. StrictMode replays effects as setup,
  // cleanup, setup without re-rendering in between, so an effect that only cleared would leave the
  // snapshot empty while the provider is still mounted. Republishing is idempotent — the same
  // bundle for the same preferences — and the render-time publish above still covers children that
  // format in their own first effect, which runs before this one.
  useEffect(() => {
    if (!isRoot) return;

    publishFormatSnapshot(value.format, enabled);

    return () => clearFormatSnapshot(value.format);
  }, [isRoot, value.format, enabled]);

  // Reporting is deduplicated for the life of the page, so re-running it costs nothing and a
  // payload that resolves to the same signature by a different route still gets reported.
  useEffect(() => {
    reportPreferenceIssues(issues);
  }, [issues]);

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};

export default LocalizationProvider;
